import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import Cookies from "js-cookie";
import { chooseModifierDefaultSelection } from "./catalogModifierDefaults";
import { valueIsDefined } from "./utils";

dayjs.extend(timezone)

// Data: any group with option values (a checkbox included) persists and prices by its selected option.
const groupHasOptionValues = (group) => {
    return (group.modifiers || []).length > 0
}

// Rendering: a checkbox is its own control even though it carries its two state options.
export const groupIsChoiceBased = (group) => {
    return group.modifier_type !== "checkbox" && groupHasOptionValues(group)
}

export const getCheckboxCheckedOption = (group) => {
    return (group.modifiers || []).find(modifier => modifier.is_checked_state)
}

export const getCatalogSelectedOption = (group, selections) => {
    const selectedValue = selections[group.external_id]

    if (selectedValue && groupHasOptionValues(group)) {
        return (group.modifiers || []).find(modifier => String(modifier.external_id) === String(selectedValue)) || null
    } else {
        return null
    }
}

export const getCatalogAdjustedPrice = (basePrice, groups, selections) => {
    // Each adjuster is applied against the base price, so the result does not depend on group order.
    const parsedBasePrice = parseFloat(basePrice)

    if (Number.isNaN(parsedBasePrice)) {
        return null
    } else {
        let adjustedPrice = parsedBasePrice
        groups.forEach(group => {
            const selectedOption = getCatalogSelectedOption(group, selections)
            const adjustmentValue = parseFloat(selectedOption?.price_adjustment_value)

            if (selectedOption?.price_adjustment_type === "relative" && !Number.isNaN(adjustmentValue)) {
                adjustedPrice += adjustmentValue
            } else if (selectedOption?.price_adjustment_type === "percentage" && !Number.isNaN(adjustmentValue)) {
                adjustedPrice += parsedBasePrice * (adjustmentValue / 100)
            }
        })
        return adjustedPrice
    }
}

export const buildCatalogModifierTemplateContext = ({ parentName, parent, selectedClient, selectedServiceLocation, selectedVendor, selectedEquipment, lineItem, preferredTimezone }) => {
    // Templates see the parent under its own type key and the line under `line_item`, each in its API shape
    // with the id-only relations swapped for the objects the container already holds, so a row written
    // against `estimate.*` cannot fire on any other line context.
    const parentContext = { ...parent }

    if (valueIsDefined(selectedServiceLocation)) {
        parentContext.service_location = { ...selectedServiceLocation, external_client: selectedClient }
    }
    if (valueIsDefined(selectedVendor)) {
        parentContext.vendor = selectedVendor
    }

    const context = {
        today: dayjs().tz(preferredTimezone).format("YYYY-MM-DD"),
        line_item: {
            ...lineItem,
            equipment: (selectedEquipment || []).find(equipment => String(equipment.id) === String(lineItem.equipment)),
        },
    }

    if (valueIsDefined(parentName)) {
        context[parentName] = parentContext
    }

    return context
}

const getDefaultCatalogModifierSelection = (group, modifierDefaults, templateContext) => {
    // A company template outranks the store's own prefill; the store default stays the fallback when no
    // row renders for the group.
    const templateSelection = chooseModifierDefaultSelection(group, modifierDefaults, templateContext)
    if (templateSelection !== null) {
        return templateSelection
    }

    // A checkbox's two options are its checked and unchecked states, so its default comes from the
    // group-level flag rather than an option's own pre-selection marker.
    if (group.modifier_type === "checkbox") {
        const checkedOption = getCheckboxCheckedOption(group)
        return group.is_checked_by_default && checkedOption ? String(checkedOption.external_id) : ""
    }

    // A text entry seeds the store's default value, mirroring the storefront where an untouched
    // prefill is submitted as-is.
    if (!groupIsChoiceBased(group)) {
        return group.default_value || ""
    }

    const defaultOption = (group.modifiers || []).find(modifier => modifier.is_selected_by_default)

    if (defaultOption) {
        return String(defaultOption.external_id)
    } else {
        return ""
    }
}

export const buildCatalogModifierSelections = (groups, savedPlatformOptions, modifierDefaults, templateContext) => {
    const savedByGroup = new Map(
        (savedPlatformOptions || []).map(option => [String(option.modifier_external_id), option]),
    )
    const selections = {}
    groups.forEach(group => {
        const savedOption = savedByGroup.get(String(group.external_id))
        if (savedOption) {
            selections[group.external_id] = String(savedOption.value)
        } else {
            selections[group.external_id] = getDefaultCatalogModifierSelection(group, modifierDefaults, templateContext)
        }
    })
    return selections
}

export const buildPlatformOptions = (groups, selections) => {
    const platformOptions = []
    groups.forEach(group => {
        const selectedValue = selections[group.external_id]

        if (selectedValue && groupHasOptionValues(group)) {
            const selectedOption = getCatalogSelectedOption(group, selections)
            // A stale saved value that no longer names one of the store's options is dropped.
            if (selectedOption) {
                platformOptions.push({
                    modifier_external_id: group.external_id,
                    modifier_name: group.name,
                    value: selectedOption.external_id,
                    value_label: selectedOption.name,
                    price_adjustment: selectedOption.price_adjustment_type
                        ? { type: selectedOption.price_adjustment_type, value: selectedOption.price_adjustment_value }
                        : null,
                })
            }
        } else if (selectedValue) {
            platformOptions.push({
                modifier_external_id: group.external_id,
                modifier_name: group.name,
                value: selectedValue,
                value_label: selectedValue,
                price_adjustment: null,
            })
        }
    })
    return platformOptions
}

export const resolveCatalogItem = async (pricebookItemID, vendorID) => {
    // A part only has store options while it still tracks a connected catalog item in the vendor's catalog;
    // without one the server answers 404, which resolves to null.
    const params = new URLSearchParams()
    params.append("pricebook_item", pricebookItemID)
    params.append("vendor", vendorID)
    const endpoint = DjangoUrls["catalogs:api-catalogs-links-resolve"](window.MARKETPLACE_ENTITY_SLUG) + `?${params}`
    const response = await fetch(endpoint)

    if (response.ok) {
        return await response.json()
    } else {
        return null
    }
}

export const fetchCatalogModifierDefaults = async (catalogID) => {
    const params = new URLSearchParams()
    params.append("catalog", catalogID)
    const endpoint = DjangoUrls["catalogs:api-catalogs-modifier-defaults-list"](window.MARKETPLACE_ENTITY_SLUG) + `?${params}`
    const response = await fetch(endpoint)

    if (!response.ok) {
        throw new Error("Could not load product option defaults.")
    }

    return await response.json()
}

export const fetchCatalogModifierGroups = async (catalogItemID) => {
    const endpoint = DjangoUrls["catalogs:api-catalogs-items-modifiers"](window.MARKETPLACE_ENTITY_SLUG, catalogItemID)
    const response = await fetch(endpoint)

    if (!response.ok) {
        throw new Error("Could not load product options.")
    }

    const body = await response.json()
    return body.modifier_groups || []
}

export const fetchCatalogStorePrice = async (catalogItemID, groups, selections) => {
    // Only choice selections are sent: they carry the option value ids a store prices by, while text,
    // number, and date entries never affect price. A store that cannot be reached, or returns no price,
    // resolves to null so the caller keeps the adjuster-based estimate.
    const options = []
    groups.forEach(group => {
        const selectedOption = getCatalogSelectedOption(group, selections)
        if (selectedOption) {
            options.push({ modifier_external_id: group.external_id, value_external_id: selectedOption.external_id })
        }
    })

    const endpoint = DjangoUrls["catalogs:api-catalogs-items-price"](window.MARKETPLACE_ENTITY_SLUG, catalogItemID)
    const headers = new Headers()
    headers.append("X-CSRFToken", Cookies.get("csrftoken"))
    headers.append("Accept", "application/json")
    headers.append("Content-Type", "application/json")

    try {
        const response = await fetch(endpoint, { method: "POST", headers: headers, body: JSON.stringify({ options }), credentials: "same-origin" })

        if (response.ok) {
            const body = await response.json()
            return body.price
        } else {
            return null
        }
    } catch (error) {
        return null
    }
}
